import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { useCountUp } from "@/lib/useCountUp";
import { useCardTilt } from "@/lib/useCardTilt";
import { api } from "@/lib/api";
import {
  Star, Flame, ShieldCheck, TrendingUp, Sparkles, Zap, AlertTriangle,
} from "lucide-react";

// ---------- Financial Health Score (composite meter + weekly WoW pulse) ----------
export function FinancialHealthScore({ stats, receipts, onPulse }) {
  // Sub-scores (0-100) — same math as the three gauge widgets below
  const gross = Number(stats?.total_spent || 0);
  const deductible = Number(stats?.deductible || 0);
  const efficiency = gross > 0 ? Math.min(100, (deductible / gross) * 100) : 0;

  const spend = Number(stats?.month_spent || 0) || (receipts || []).reduce((a, r) => {
    const monthKey = new Date().toISOString().slice(0, 7);
    const d = (r.date || r.created_at || "").slice(0, 7);
    return d === monthKey ? a + Number(r.total || 0) : a;
  }, 0);
  const BUDGET_CAP = 10000;
  const burnHealth = Math.max(0, 100 - Math.min(100, (spend / BUDGET_CAP) * 100));

  const list = receipts || [];
  const complete = list.filter((r) =>
    !!(r.merchant && String(r.merchant).trim()) &&
    Number(r.total || 0) > 0 &&
    !!(r.date || r.created_at) &&
    !!(r.category && String(r.category).trim())
  ).length;
  const readiness = list.length > 0 ? (complete / list.length) * 100 : 100;

  // Equal-weight composite (rounded)
  const composite = Math.round((efficiency + burnHealth + readiness) / 3);
  const animated = useCountUp(composite, 1100);
  const [history, setHistory] = useState([]);
  const postedRef = useRef(false);

  // Snapshot (fires exactly ONCE per mount; server-side is idempotent per ISO week) + history
  useEffect(() => {
    if (!stats) return;
    if (postedRef.current) return;
    postedRef.current = true;
    (async () => {
      try {
        await api.post("/dashboard/health-score/snapshot", {
          composite,
          deduction_efficiency: Math.round(efficiency),
          burn_health: Math.round(burnHealth),
          cpa_readiness: Math.round(readiness),
        });
        const { data } = await api.get("/dashboard/health-score/history?weeks=8");
        setHistory(data?.snapshots || []);
        // WoW improvement pulse — last snapshot vs previous
        const snaps = data?.snapshots || [];
        if (snaps.length >= 2 && snaps[0].composite > snaps[1].composite) {
          onPulse?.(true);
          setTimeout(() => onPulse?.(false), 4200);
        }
      } catch (_) { /* silent */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats, composite]);

  const tier = composite >= 80 ? { label: "Elite", color: "#10B981", glow: "rgba(16,185,129,0.6)" }
             : composite >= 60 ? { label: "Strong", color: "#22d3ee", glow: "rgba(34,211,238,0.55)" }
             : composite >= 40 ? { label: "Building", color: "#eab308", glow: "rgba(234,179,8,0.5)" }
             :                    { label: "Needs work", color: "#f43f5e", glow: "rgba(244,63,94,0.5)" };

  // WoW delta from history
  let deltaText = null, deltaClass = "text-slate-500";
  if (history.length >= 2) {
    const diff = Math.round(history[0].composite - history[1].composite);
    if (diff > 0) { deltaText = `+${diff} vs last week`; deltaClass = "text-emerald-300"; }
    else if (diff < 0) { deltaText = `${diff} vs last week`; deltaClass = "text-rose-300"; }
    else { deltaText = "flat vs last week"; deltaClass = "text-slate-500"; }
  }

  // Streak — sourced from the newest history snapshot's persisted `streak` field
  const streak = Number(history?.[0]?.streak || 0);

  // 3D parallax tilt + numeric-change ripple (fires on composite total change)
  const { bind, Ripples } = useCardTilt({ pulseKey: composite });

  // Broadcast a constellation-trigger when the health score crosses a
  // milestone tier (>=80 Elite / >=60 Strong) so the SpaceAgeControlDeck
  // canvas can bridge neighboring stars into a temporary constellation.
  // On Elite (>=80) we additionally emit `elite-burst-at` with the card
  // wrapper's live bounding-rect center so the top-level EliteBurstCanvas
  // can erupt the 40 neon-cyan + gold particles directly outward from
  // this widget instead of the viewport center.
  const lastMilestoneRef = useRef(0);

  // ---- Elite Origin History — last 5 crossings into the Elite tier (>=80) ----
  const [eliteHistory, setEliteHistory] = useState(() => {
    try {
      const raw = window.localStorage.getItem("rs.health.eliteCrossings");
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.slice(-5) : [];
    } catch (_) { return []; }
  });
  const wasEliteRef = useRef(composite >= 80);

  // Publish the composite score to `window.__rsHealthComposite` so ambient
  // canvases (e.g. CosmicStaticTouchLayer's elite-combo tripper) can gate
  // milestone effects without prop-drilling or context subscriptions.
  useEffect(() => {
    if (typeof window !== "undefined") window.__rsHealthComposite = Number(composite || 0);
  }, [composite]);


  useEffect(() => {
    if (typeof window === "undefined") return;
    const milestone = composite >= 80 ? 80 : composite >= 60 ? 60 : 0;
    if (milestone && milestone > lastMilestoneRef.current) {
      lastMilestoneRef.current = milestone;
      window.dispatchEvent(new CustomEvent("constellation-trigger", { detail: { source: "health-score", milestone } }));
      if (milestone >= 80 && bind.ref?.current) {
        const rect = bind.ref.current.getBoundingClientRect();
        window.dispatchEvent(new CustomEvent("elite-burst-at", {
          detail: { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 },
        }));
      }
    }
    // Elite Origin History — persist a fresh entry on every rising-edge
    // transition INTO the Elite tier (composite crosses from <80 → >=80).
    const nowElite = composite >= 80;
    if (nowElite && !wasEliteRef.current) {
      const entry = { score: composite, ts: new Date().toISOString() };
      setEliteHistory((prev) => {
        const next = [...prev, entry].slice(-5);
        try { window.localStorage.setItem("rs.health.eliteCrossings", JSON.stringify(next)); } catch (_) { /* silent */ }
        return next;
      });
    }
    wasEliteRef.current = nowElite;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composite]);

  // Hover state for the Elite Origin History popover
  const [showEliteHistory, setShowEliteHistory] = useState(false);
  const eliteHistoryLabel = eliteHistory.length
    ? `${eliteHistory.length} Elite ${eliteHistory.length === 1 ? "crossing" : "crossings"}`
    : "No Elite crossings yet";

  // ---- 8-week sparkline points (oldest → newest, left → right) ----
  // history[] arrives newest-first from the backend; reverse for chronological x-axis.
  const spark = (history || []).slice().reverse().map((h, i) => ({
    x: i,
    y: Math.max(0, Math.min(100, Number(h?.composite || 0))),
  }));
  const SPARK_W = 220;
  const SPARK_H = 44;
  const sparkPath = (() => {
    if (spark.length < 2) return "";
    const n = spark.length;
    const stepX = SPARK_W / Math.max(1, n - 1);
    return spark
      .map((p, i) => `${i === 0 ? "M" : "L"} ${(i * stepX).toFixed(1)} ${(SPARK_H - (p.y / 100) * SPARK_H).toFixed(1)}`)
      .join(" ");
  })();
  const sparkAreaPath = sparkPath ? `${sparkPath} L ${SPARK_W} ${SPARK_H} L 0 ${SPARK_H} Z` : "";

  return (
    <div
      {...bind}
      data-testid="financial-health-score"
      className="glass cosmic-aura cosmic-aura-cyan cosmic-sheen rounded-2xl p-6 mb-6 fade-up-widget relative overflow-hidden"
      style={{ ...bind.style, animationDelay: "0ms", borderColor: "rgba(34,211,238,0.28)" }}
    >
      <Ripples />
      {/* Ambient glow behind the number */}
      <div
        aria-hidden="true"
        className="absolute -top-16 -right-16 w-64 h-64 rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${tier.glow} 0%, transparent 60%)`, opacity: 0.35 }}
      />
      <div className="relative flex flex-col md:flex-row md:items-center gap-6">
        {/* LEFT — composite number */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-emerald-400 flex items-center gap-1.5">
              <Sparkles size={11} /> Financial Health Score
            </div>
            <span
              data-testid="health-score-tier"
              onMouseEnter={() => setShowEliteHistory(true)}
              onMouseLeave={() => setShowEliteHistory(false)}
              className="relative text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border cursor-help"
              style={{ borderColor: tier.color + "66", color: tier.color, background: tier.color + "1A" }}
            >
              {tier.label}
              {showEliteHistory && (
                <div
                  data-testid="elite-history-popover"
                  className="absolute left-0 top-full mt-2 z-40 bg-slate-900/90 backdrop-blur-md rounded-xl p-3 border border-cyan-500/20 w-[260px] text-slate-200"
                  style={{ boxShadow: "0 12px 40px -8px rgba(34,211,238,0.35)" }}
                >
                  <div className="text-[9px] font-mono uppercase tracking-[0.28em] text-cyan-300 mb-2 flex items-center gap-1.5">
                    <Star size={10} /> Elite Origin History
                  </div>
                  {eliteHistory.length === 0 ? (
                    <div className="text-[11px] text-slate-400 font-mono">
                      No Elite crossings yet. Cross composite 80 to log the first origin.
                    </div>
                  ) : (
                    <>
                      <div className="flex items-end gap-2 h-[46px] mb-2" data-testid="elite-history-timeline">
                        {eliteHistory.map((h, i) => {
                          const s = Math.max(0, Math.min(100, Number(h.score || 0)));
                          const barH = 6 + (s - 60) * 0.9; // scale 60..100 → 6..42px
                          return (
                            <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1">
                              <div
                                className="w-full rounded-t"
                                style={{
                                  height: `${Math.max(4, barH)}px`,
                                  background: "linear-gradient(180deg, #22d3ee, #10b981)",
                                  boxShadow: "0 0 8px rgba(34,211,238,0.55)",
                                }}
                                title={`Score ${s} @ ${new Date(h.ts).toLocaleString()}`}
                              />
                              <div className="text-[8px] font-mono text-slate-500">{s}</div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500 border-t border-white/5 pt-1.5 flex items-center justify-between">
                        <span>{eliteHistoryLabel}</span>
                        <span className="text-slate-600">last 5</span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </span>
            {streak >= 1 && (
              <span
                data-testid="health-streak-badge"
                title={`${streak}-week improvement streak`}
                className="health-streak-flame inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-mono uppercase tracking-widest"
                style={{
                  borderColor: "rgba(249,115,22,0.55)",
                  background: "linear-gradient(90deg, rgba(249,115,22,0.18), rgba(234,88,12,0.08))",
                  color: "#fb923c",
                }}
              >
                <Flame size={11} style={{ filter: "drop-shadow(0 0 4px rgba(249,115,22,0.9))" }} />
                <span data-testid="health-streak-count">{streak}</span>
                <span className="opacity-70">wk streak</span>
              </span>
            )}
          </div>
          <div className="flex items-baseline gap-3">
            <div
              data-testid="health-score-value"
              className="font-bold font-mono leading-none cosmic-digit-glow-cyan"
              style={{
                fontSize: "72px",
                color: tier.color,
                textShadow: `0 0 24px ${tier.glow}, 0 0 48px ${tier.glow}`,
              }}
            >
              {Math.round(animated)}
            </div>
            <div className="text-slate-500 font-mono text-xl cosmic-digit-glow-cyan">/ 100</div>
          </div>
          {deltaText && (
            <div
              data-testid="health-score-delta"
              className={`mt-2 text-[11px] font-mono uppercase tracking-widest ${deltaClass}`}
            >
              {deltaText}
            </div>
          )}
        </div>
        {/* MIDDLE — 8-week composite trajectory sparkline */}
        <div
          data-testid="health-score-sparkline"
          className="hidden md:flex flex-col items-start justify-center min-w-[240px] px-1"
          aria-label={`8-week health score trend, ${spark.length} weekly snapshots`}
        >
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">
            Last 8 weeks
          </div>
          {spark.length >= 2 ? (
            <svg width={SPARK_W} height={SPARK_H} viewBox={`0 0 ${SPARK_W} ${SPARK_H}`} className="overflow-visible">
              <defs>
                <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={tier.color} stopOpacity="0.35" />
                  <stop offset="100%" stopColor={tier.color} stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={sparkAreaPath} fill="url(#sparkFill)" />
              <path d={sparkPath} fill="none" stroke={tier.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ filter: `drop-shadow(0 0 4px ${tier.glow})` }} />
              {spark.map((p, i) => (
                <circle key={i}
                        cx={(i * (SPARK_W / Math.max(1, spark.length - 1))).toFixed(1)}
                        cy={(SPARK_H - (p.y / 100) * SPARK_H).toFixed(1)}
                        r={i === spark.length - 1 ? 3.5 : 2}
                        fill={i === spark.length - 1 ? tier.color : "#0f172a"}
                        stroke={tier.color} strokeWidth="1.2" />
              ))}
            </svg>
          ) : (
            <div className="text-[11px] text-slate-500 font-mono h-[44px] flex items-center">
              Building history — check back next week.
            </div>
          )}
          <div className="mt-1.5 flex items-center justify-between w-full text-[9px] font-mono uppercase tracking-widest text-slate-600">
            <span>8w ago</span>
            <span>now</span>
          </div>
        </div>
        {/* RIGHT — sub-score breakdown */}
        <div className="flex-1 grid grid-cols-3 gap-3" data-testid="health-score-breakdown">
          {[
            { key: "deduction", label: "Deduction", value: Math.round(efficiency) },
            { key: "burn", label: "Burn health", value: Math.round(burnHealth) },
            { key: "readiness", label: "CPA-ready", value: Math.round(readiness) },
          ].map((s) => (
            <div key={s.key} data-testid={`health-sub-${s.key}`} className="rounded-lg bg-white/[0.03] border border-white/5 px-3 py-3">
              <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">{s.label}</div>
              <div className="text-white font-mono font-semibold text-2xl mt-1">{s.value}</div>
              <div className="mt-1.5 h-1 rounded-full bg-white/10 overflow-hidden">
                <div
                  className={`h-full rounded-full ${s.value >= 60 ? "cosmic-progress-neon" : ""}`}
                  style={{
                    width: `${s.value}%`,
                    ...(s.value >= 60
                      ? {}
                      : { background: s.value >= 30 ? "#eab308" : "#f43f5e" }),
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


